
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { category, CategoryInfo } from '@reflact/ipkv-types';
import { Breadcrumb } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { MessageService } from 'primeng/api';
import { ReducedEditor } from '../../../components/layout/reduced-editor/reduced-editor';
import { CategoryInfoService } from '../../../services/category-info-service';

type CategoryInfoEditorModel = CategoryInfo & {
  vcBotIntroductions: string;
  vcIntroductions: { title: string; content: string }[];
  FallaufgabeOutro: string;
  vcOutro: string;
  vcSampleRate: number;
};

@Component({
  imports: [FormsModule, Breadcrumb, ButtonModule, InputNumberModule, TextareaModule, ReducedEditor, TabsModule],
  templateUrl: './category-info.html',
  styleUrl: './category-info.scss',
})
export class CategoryInfoEditor implements OnInit {
  protected kategorie: category | null = null;
  protected isLoading = false;
  protected categoryInfo: CategoryInfoEditorModel | null = null;

  private route = inject(ActivatedRoute);
  private categoryInfoService = inject(CategoryInfoService);
private messageService = inject(MessageService);

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.kategorie = params.get('kategorie') as category | null;
      void this.loadCategoryInfo();
    });
  }

  protected addIntroduction(mode: 'exam' | 'vc'): void {
    const intros = this.getIntroductions(mode);
    if (intros == null) {
      return;
    }
    intros.push({ title: '', content: '' });
  }

  protected removeIntroduction(mode: 'exam' | 'vc', index: number): void {
    const intros = this.getIntroductions(mode);
    if (intros == null) {
      return;
    }
    intros.splice(index, 1);
  }

  protected moveIntroductionUp(mode: 'exam' | 'vc', index: number): void {
    const arr = this.getIntroductions(mode);
    if (arr == null || index <= 0) {
      return;
    }
    [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
  }

  protected moveIntroductionDown(mode: 'exam' | 'vc', index: number): void {
    const arr = this.getIntroductions(mode);
    if (arr == null || index >= arr.length - 1) {
      return;
    }
    [arr[index + 1], arr[index]] = [arr[index], arr[index + 1]];
  }

  protected async save(): Promise<void> {
    if (this.categoryInfo == null) {
      return;
    }

    try {
      this.categoryInfo.vcSampleRate = this.normalizeVcSampleRate(this.categoryInfo.vcSampleRate);

      this.categoryInfo = (await this.categoryInfoService.update({
        _id: this.categoryInfo._id,
        botIntroductions: this.categoryInfo.botIntroductions,
        fallAufgabeIntroductions: this.categoryInfo.fallAufgabeIntroductions,
        vcBotIntroductions: this.categoryInfo.vcBotIntroductions,
        vcIntroductions: this.categoryInfo.vcIntroductions,
        FallaufgabeOutro: this.categoryInfo.FallaufgabeOutro,
        vcOutro: this.categoryInfo.vcOutro,
        vcSampleRate: this.categoryInfo.vcSampleRate,
      })) as CategoryInfoEditorModel;

      this.ensureDefaults(this.categoryInfo);

      this.messageService.add({
        severity: 'success',
        summary: 'Erfolgreich',
        detail: 'Kategorieinfo gespeichert',
        life: 3000,
      });
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Kategorieinfo konnte nicht gespeichert werden',
      });
      console.error('Error saving category info:', error);
    }
  }

  private async loadCategoryInfo(): Promise<void> {
    if (this.kategorie == null) {
      return;
    }

    this.isLoading = true;
    try {
      const list = await this.categoryInfoService.list(this.kategorie);
      this.categoryInfo = (list[0] ?? null) as CategoryInfoEditorModel | null;
      if (this.categoryInfo != null) {
        this.ensureDefaults(this.categoryInfo);
      }
    } finally {
      this.isLoading = false;
    }
  }

  private getIntroductions(mode: 'exam' | 'vc'): { title: string; content: string }[] | null {
    if (this.categoryInfo == null) {
      return null;
    }
    return mode === 'vc' ? (this.categoryInfo.vcIntroductions ??= []) : this.categoryInfo.fallAufgabeIntroductions;
  }

  private ensureDefaults(info: CategoryInfoEditorModel): void {
    info.vcBotIntroductions ??= '';
    info.vcIntroductions ??= [];
    info.FallaufgabeOutro ??= '';
    info.vcOutro ??= '';
    info.vcSampleRate = this.normalizeVcSampleRate(info.vcSampleRate);
  }

  private normalizeVcSampleRate(value: unknown): number {
    const numeric = typeof value === 'number' ? value : Number(value ?? 10);
    if (!Number.isFinite(numeric)) {
      return 10;
    }
    return Math.max(1, Math.floor(numeric));
  }
}
